/**
 * DiagnosticsToolRunner - Runs diagnostic tests by dispatching tools
 *
 * This module provides a content-script-compatible way to run diagnostics
 * that uses the official tools from @common/tools as the single authority.
 *
 * Unlike test-executors.ts (which runs in page context and calls WaCommunicationLayerClient directly),
 * this runner dispatches Redux thunks that handle dual-mode execution properly.
 *
 * IMPORTANT: Import directly from source thunk files to avoid circular dependency with @common/tools/whatsapp/index.ts
 * (notifier-handlers.ts imports DiagnosticsToolRunner, and index.ts imports notifier-handlers.ts)
 */

import { chatKindOf } from '@bt-common/schemas/wa/chat-kind';
import { AppThunkDispatch } from '@common/types/common-root-state-type';
import { getStore } from '@common/state/state-provider';

import { selectUserId } from '@common/reducers/UserSelectors';
import { MessageState } from '@common/types/interface';
import { UserMessage } from '@bt-common/schemas/bt/user-message';
import { scheduleMessageTool } from '@common/thunks/UserMessagesThunks';
// Import directly from source files to avoid circular dependency
import {
    // Validation tools
    waValidatePhoneNumberOrChatIdTool,
    waGetLinkPreviewTool,
    // Contact tools
    waGetProfilePicUrlTool,
    // Chat tools
    waGetChatObjectByChatIdTool,
    waGetChatParticipantsTool,
    waOpenChatTool,
    // Group tools
    waCreateGroupTool,
    waUpdateGroupNameTool,
    waGetGroupDataTool,
    waGetCommonGroupsTool,
    waUpdateGroupDescriptionTool,
    waAddGroupParticipantTool,
    waRemoveGroupParticipantTool,
    waSetGroupAdminRightsTool,
    waRemoveGroupAdminRightsTool,
    waSetGroupPictureTool,
    waLeaveGroupTool,
    // Unified Contact/Chat Query tool (replaces waGetContactsTool, waSearchContactsTool, waGetChatsTool, waSearchContactsAndChatsTool, waGetChatsAndContactsTool)
    waQueryContactsAndChatsTool,
    waGetLatestChatsAndMessagesTool,
    waQueryMessagesTool,
    // Load Messages From Phone tool (standalone tool for loading older messages)
    waLoadMessagesFromPhoneTool,
    // Message tools (waGetMessagesTool, waSearchMessagesTool removed - use waQueryMessagesTool)
    waGetMessageByKeyTool,
    waGetUnloggedWaMessageKeyIfExistInStoreTool,
    waReactToMessageThunkTool,
    waForwardMessageTool,
    waStarMessageTool,
    waDeleteMessageTool,
    waPinMessageTool,
    waUnpinMessageTool,
    createStatusTool,

    // Media tools
    waGetMessageMediaTool,
    // Session tools
    getWaMeInfoTool,
    isWhatsAppConnectedThunkTool,
    waValidateStoresTool,
    waTriggerQRTimeoutTool,
    waTriggerLogoutTool,
    waReloadPageTool,
    // Label tools
    waUpdateShouldAppearChatsByListTool,
} from '@extension/context/content/thunks/WhatsappThunks';
import {
    sendMessageThunkTool,
} from '@extension/context/content/thunks/WhatsappSendMessageThunk';
import { WA_TOOL_COMMANDS, unwrapEngineResult } from '@bt-common/interfaces/whatsapp-tools-commands.interface';
// Newsletter tools have no dual-mode thunk wrapper, so the executors below
// route the same two ways every other tool does: in the extension page context
// reach the page handlers via the client actions; otherwise (e.g. the website
// diagnostics at :3010, which runs in SERVER mode) go through the backend →
// MQTT → engine. Importing the client-actions class directly — not the
// @common/tools index — keeps this clear of the notifier-handlers ⇄
// DiagnosticsToolRunner circular dependency.
import { WhatsappToolsClientActions } from '@common/tools/whatsapp/wa-tools-client-actions';
import { triggerRequesthWhatsappTool } from '@common/tools/whatsapp/wa-tools-server-actions';
import { isWhatsappExtensionContext } from '@common-services/detect-context';
import type {
    WaGetNewsletterResult,
} from '@bt-common/interfaces/whatsapp-tool-contracts.interface';
import type { WaToolArgs } from '@bt-common/interfaces/whatsapp-tool-contracts.interface';
import { waGetMessageAckTool } from '@extension/context/content/thunks/WhatsappThunks';
import {
    waSendSeenTool,
    waClearMessagesTool,
    waDeleteChatTool,
    waArchiveChatTool,
    waUnarchiveChatTool,
    waPinChatTool,
    waUnpinChatTool,
    waMuteChatTool,
    waUnmuteChatTool,
    waMarkChatUnreadTool,
    waSendStateTypingTool,
    waSendStateRecordingTool,
    waClearStateTool,
    waGetChatLabelsTool,
    waChangeLabelsTool,
    waGetPinnedMessagesTool,
    waAddOrEditCustomerNoteTool,
} from '@common/tools/whatsapp/chat/chat-tools';

import type {
    DiagnosticRunOptions,
    DiagnosticRunResult,
    DiagnosticTestResult,
    DiagnosticTestDefinition,
    TestSafety,
} from '@wa-communication-layer/diagnostics/test-registry';
import { diagnosticTests } from '@wa-communication-layer/diagnostics/test-registry';
import { findFirstUrlInText, buildLinkPreview, type EngineLinkPreviewResult } from '@extension/context/content/thunks/link-preview-build';
import { DIAGNOSTIC_TEST_IMAGE_DATA_URL } from '@wa-communication-layer/diagnostics/test-media-constants';
import { resolveAllDefaults, resolveDefault, type ResolvedDefaults } from '@wa-communication-layer/diagnostics/smart-defaults';
import { buildQueryContactsAndChatsParams } from '@wa-communication-layer/diagnostics/query-params-builder';

const DEFAULT_TEST_TIMEOUT_MS = 15_000;

const normalizeChatId = (chatId: any): string =>
    String(chatId ?? '')
        .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, '')
        .trim();


const normalizeScheduleDueDate = (dueDateInput: any): string => {
    const parsed = dueDateInput ? new Date(dueDateInput) : new Date(Date.now() + 60 * 60 * 1000);
    if (Number.isNaN(parsed.getTime())) {
        throw new Error('Invalid dueDate. Expected an ISO date string.');
    }
    return parsed.toISOString();
};

const getMimeTypeFromDataUrl = (dataUrl: string): string => {
    const match = /^data:([^;]+);base64,/.exec(dataUrl);
    return match?.[1] || 'image/png';
};

const createDiagnosticObjectId = (): string => {
    const timestampHex = Math.floor(Date.now() / 1000).toString(16).padStart(8, '0');
    let randomHex = '';
    for (let i = 0; i < 16; i += 1) {
        randomHex += Math.floor(Math.random() * 16).toString(16);
    }
    return `${timestampHex}${randomHex}`.slice(0, 24);
};

const parsePollOptions = (pollOptionsCsv: any): string[] =>
    String(pollOptionsCsv ?? '')
        .split(',')
        .map((option: string) => option.trim())
        .filter((option: string) => option.length > 0);

const parseCsvValues = (value: any): string[] =>
    String(value ?? '')
        .split(',')
        .map((item: string) => item.trim())
        .filter((item: string) => item.length > 0);

const resolveMessageKeyInput = (inputs: Record<string, any>): string =>
    String(inputs?.waMessageKey || inputs?.messageKey || inputs?.messageKay || inputs?.key || inputs?.messageId || '').trim();

const parseGroupSettingsInput = (value: any): Record<string, any> => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value;
    }

    const raw = String(value ?? '').trim();
    if (!raw) {
        return {};
    }

    // Backward compatibility: still support JSON payload in diagnostics.
    if (raw.startsWith('{')) {
        try {
            const parsed = JSON.parse(raw);
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed;
            }
            return {
                __mockSettingsText: raw,
                __mockReason: 'non-object-json',
            };
        } catch {
            return {
                __mockSettingsText: raw,
                __mockReason: 'invalid-json',
            };
        }
    }

    return {
        __mockSettingsText: raw,
        __mockReason: 'raw-text',
    };
};

const blobToDataUrl = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('Failed to convert blob to data URL'));
        reader.readAsDataURL(blob);
    });

/** Derive a filename from a URL's path, or '' when it has none. */
const fileNameFromUrl = (url: string): string => {
    try {
        const name = new URL(url).pathname.split('/').pop() || '';
        return name.split('?')[0] || '';
    } catch {
        return '';
    }
};

/**
 * Fetch a file URL and convert it to a data URL (blob) so it sends exactly like
 * an uploaded file. Returns the data URL plus the resolved mime type and a
 * filename derived from the URL. Throws on network / CORS / non-OK responses —
 * the caller decides whether to fall back to passing the raw URL through.
 */
const fetchFileUrlAsDataUrl = async (
    url: string,
): Promise<{ dataUrl: string; mimeType: string; fileName: string }> => {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error(`HTTP ${response.status} fetching file URL`);
    }
    const blob = await response.blob();
    const dataUrl = await blobToDataUrl(blob);
    return {
        dataUrl,
        mimeType: blob.type || getMimeTypeFromDataUrl(dataUrl),
        fileName: fileNameFromUrl(url) || 'diagnostic-file',
    };
};

const loadImageFromDataUrl = (dataUrl: string): Promise<HTMLImageElement> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Failed to load image from data URL'));
        img.src = dataUrl;
    });

const shrinkGroupPictureDataUrl = async (dataUrl: string): Promise<string> => {
    if (!/^data:image\//i.test(dataUrl)) {
        return dataUrl;
    }

    // Keep small payloads as-is.
    if (dataUrl.length <= 350_000) {
        return dataUrl;
    }

    try {
        const img = await loadImageFromDataUrl(dataUrl);
        const srcWidth = img.naturalWidth || img.width;
        const srcHeight = img.naturalHeight || img.height;
        if (!srcWidth || !srcHeight) {
            return dataUrl;
        }

        const maxSide = 720;
        const scale = Math.min(1, maxSide / Math.max(srcWidth, srcHeight));
        const targetWidth = Math.max(1, Math.round(srcWidth * scale));
        const targetHeight = Math.max(1, Math.round(srcHeight * scale));

        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return dataUrl;
        }
        ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

        const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.82);
        if (!jpegDataUrl || !/^data:image\//i.test(jpegDataUrl)) {
            return dataUrl;
        }

        return jpegDataUrl.length < dataUrl.length ? jpegDataUrl : dataUrl;
    } catch {
        return dataUrl;
    }
};

const ensureGroupPictureFileDataUrl = async (rawValue: any): Promise<string> => {
    const value = String(rawValue || '').trim();
    if (!value) {
        throw new Error('fileDataUrl is required');
    }

    if (/^data:/i.test(value)) {
        return shrinkGroupPictureDataUrl(value);
    }

    if (!/^https?:\/\//i.test(value)) {
        throw new Error('fileDataUrl must be a data URL or public HTTP(S) URL');
    }

    try {
        const response = await fetch(value);
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const dataUrl = await blobToDataUrl(blob);
        return shrinkGroupPictureDataUrl(dataUrl);
    } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        throw new Error(
            `Failed to download remote image URL for group picture (${message}). ` +
            'Please re-upload the file from your computer.'
        );
    }
};

type MentionParticipant = {
    id: string;
    name: string;
};

const MENTION_TRAILING_BOUNDARY = "(?=$|\\s|[.,!?;:()\\[\\]{}\"'`~\\u05F3\\u05F4])";

const escapeRegex = (value: string): string =>
    value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const buildAtMentionRegex = (token: string): RegExp =>
    new RegExp(`@${escapeRegex(token)}${MENTION_TRAILING_BOUNDARY}`, 'gu');

const buildMentionTokensFromInputs = (inputs: Record<string, any>): string[] => {
    const mentionIds = parseCsvValues(inputs.mentionIdsCsv || inputs.mentionedJidListCsv || inputs.mentionIds || inputs.mentionedJidList);
    if (mentionIds.length === 0) {
        return [];
    }

    const mentionDisplays = parseCsvValues(inputs.mentionDisplaysCsv || inputs.mentionNamesCsv || inputs.mentionDisplays);

    return mentionIds.map((mentionId: string, index: number) => {
        const fallbackDisplay = mentionId.split('@')[0] || mentionId;
        const mentionDisplay = mentionDisplays[index] || fallbackDisplay;
        return `@[${mentionDisplay}](${mentionId})`;
    });
};

const normalizeMentionParticipants = (participants: any[]): MentionParticipant[] => {
    const byName = new Map<string, MentionParticipant>();

    for (const participant of participants) {
        const id = String(participant?.id?._serialized || participant?.id || '').trim();
        const name = String(participant?.name || '').trim();
        if (!id || !name) {
            continue;
        }

        const mapKey = name.toLowerCase();
        if (!byName.has(mapKey)) {
            byName.set(mapKey, { id, name });
        }
    }

    return [...byName.values()].sort((a, b) => b.name.length - a.name.length);
};

const replaceAtNameMentionsWithStructuredTokens = (
    messageBody: string,
    participants: MentionParticipant[]
): string => {
    let nextBody = messageBody;

    for (const participant of participants) {
        const mentionToken = `@[${participant.name}](${participant.id})`;
        if (nextBody.includes(mentionToken)) {
            continue;
        }

        nextBody = nextBody.replace(buildAtMentionRegex(participant.name), mentionToken);

        const userPart = participant.id.split('@')[0];
        if (userPart) {
            nextBody = nextBody.replace(buildAtMentionRegex(userPart), mentionToken);
        }
    }

    return nextBody;
};

const normalizeDiagnosticMentions = async (
    dispatch: AppThunkDispatch,
    inputs: Record<string, any>,
    chatId: string,
    messageBody: string
): Promise<string> => {
    let nextBody = messageBody;

    // Backward compatibility for direct payloads that still send mentionIds/mentionDisplays.
    const mentionTokens = buildMentionTokensFromInputs(inputs);
    if (mentionTokens.length > 0) {
        const tokensToAppend = mentionTokens.filter((token) => !nextBody.includes(token));
        if (tokensToAppend.length > 0) {
            nextBody = `${nextBody} ${tokensToAppend.join(' ')}`.trim();
        }
    }

    if (chatKindOf(chatId) !== 'group' || !nextBody.includes('@')) {
        return nextBody;
    }

    try {
        const participantsResponse = await waGetChatParticipantsTool({ chatId });
        const participantsData: any = participantsResponse.success ? participantsResponse.data : undefined;
        // The tool now returns a paged envelope `{ participants, total,
        // limit, skip }`; accept the legacy bare-array form too.
        const participantsArray: any[] = Array.isArray(participantsData?.participants)
            ? participantsData.participants
            : Array.isArray(participantsData)
                ? participantsData
                : [];
        const participants = normalizeMentionParticipants(participantsArray);
        if (participants.length === 0) {
            return nextBody;
        }

        return replaceAtNameMentionsWithStructuredTokens(nextBody, participants);
    } catch (error) {
        console.warn('[DiagnosticsToolRunner] Failed to resolve @name mentions from chat participants:', error);
        return nextBody;
    }
};

const toBoolean = (value: any): boolean =>
    value === true || value === 'true';

const buildDiagnosticUserMessage = async (
    dispatch: AppThunkDispatch,
    inputs: Record<string, any>,
    options: { scheduled: boolean }
): Promise<UserMessage> => {
    const chatId = normalizeChatId(inputs.chatId);
    if (!chatId) {
        throw new Error('chatId is required. Set Chat ID or enable Global and provide a Global Chat ID.');
    }
    const ownerId = selectUserId(getStore().getState() as any);
    // `linkPreview` mirrors the public-API contract: ON (default) → fetch
    // and attach a rich preview for the first URL found in the body; OFF →
    // send regularly with no preview. The URL is detected from the message
    // body itself — there is no separate "Link URL" field anymore.
    const linkPreviewEnabled = inputs.linkPreview === undefined ? true : toBoolean(inputs.linkPreview);
    // Media can come from an uploaded file (`fileDataUrl`) OR a file URL
    // (`fileUrl`). An uploaded file always wins; a URL-only input is fetched and
    // converted to a blob/data URL so it sends exactly like an uploaded file.
    const uploadedFileDataUrl = String(inputs.fileDataUrl ?? '').trim();
    const fileUrlInput = String(inputs.mediaUrl ?? '').trim();
    const hasMedia = uploadedFileDataUrl.length > 0 || fileUrlInput.length > 0;
    const allowMultipleAnswers = toBoolean(inputs.pollAllowMultiple);
    const pollOptions = parsePollOptions(inputs.pollOptionsCsv);
    const hasPoll =
        String(inputs.pollQuestion || '').trim().length > 0 ||
        pollOptions.length > 0 ||
        allowMultipleAnswers;

    let messageBody = String(inputs.text || '').trim();

    messageBody = await normalizeDiagnosticMentions(dispatch, inputs, chatId, messageBody);

    const createdMessage = {
        ...(options.scheduled ? {} : { _id: createDiagnosticObjectId() }),
        owner: ownerId,
        message: messageBody,
        contactList: [{ id: chatId, name: chatId }],
        // Scheduling-only metadata: the user-message-scheduler reads
        // dueDate/status/state to pick up and deliver the row later. A direct
        // send (sendMessageThunkTool) dispatches to the engine immediately and
        // ignores these, so omit them to keep the send payload clean.
        ...(options.scheduled
            ? {
                dueDate: normalizeScheduleDueDate(inputs.dueDate),
                status: 'pending',
                state: MessageState.pending,
                disableOptimisticUpdates: true,
            }
            : {}),
    } as object as UserMessage;

    // The send/schedule reply field is `replyTo` (camelCase, mirrors the public
    // API). `messageKey`/`messageKay` remain as a backward-compat fallback for
    // older callers/overrides that still pass the legacy key.
    const replyMessageKey = String(inputs.replyTo || inputs.messageKey || inputs.messageKay || '').trim();
    if (replyMessageKey) {
        createdMessage.quotedMessageId = replyMessageKey;
        createdMessage.replyData = { _serialized: replyMessageKey };
    }

    // The send's identity/correlation token IS its `_id` — the engine encodes
    // `_id` as the WA messageSecret (markers.ts) and the send response returns
    // it back. So a caller-supplied secret is just a custom `_id`: carry it on
    // `_id` (single identity field, no separate `secret`) rather than sending
    // both. Exception: a SCHEDULED send persists to Mongo, which requires a
    // 24-hex ObjectId `_id`, so there we keep the custom secret as its own field.
    const secret = String(inputs.secret || '').trim();
    if (secret) {
        if (options.scheduled) {
            createdMessage.secret = secret;
        } else {
            (createdMessage as any)._id = secret;
        }
    }

    if (hasMedia) {
        // Uploaded file wins over a file URL. With only a URL, fetch + convert it
        // to a data URL (blob) so it sends exactly like an uploaded file; if the
        // client-side fetch is blocked (CORS / network), pass the raw URL through
        // and let the engine fetch it via MessageMedia.fromUrl.
        let assetDataUrl = uploadedFileDataUrl;
        let mimeTypeFromUrl = '';
        let nameFromUrl = '';
        if (!assetDataUrl && fileUrlInput) {
            try {
                const converted = await fetchFileUrlAsDataUrl(fileUrlInput);
                assetDataUrl = converted.dataUrl;
                mimeTypeFromUrl = converted.mimeType;
                nameFromUrl = converted.fileName;
            } catch (error) {
                console.warn('[DiagnosticsToolRunner] file URL fetch failed, passing URL through to engine:', error);
                assetDataUrl = fileUrlInput;
                nameFromUrl = fileNameFromUrl(fileUrlInput);
            }
        }
        const detectedMimeType = getMimeTypeFromDataUrl(assetDataUrl);
        const fileMimeType = String(inputs.fileMimeType || mimeTypeFromUrl || detectedMimeType || 'image/png');
        const fileName = String(inputs.mediaFilename || nameFromUrl || (fileMimeType.startsWith('video/') ? 'diagnostic-video.mp4' : 'diagnostic-image.png'));
        createdMessage.asset = {
            url: assetDataUrl,
            type: fileMimeType,
            name: fileName,
        };
    }

    if (hasPoll) {
        createdMessage.isPoll = true;
        createdMessage.poll = {
            question: String(inputs.pollQuestion || '').trim() || 'BT Diagnostics Test Poll',
            allowMultiple: allowMultipleAnswers,
            options: pollOptions.map((text, index) => ({
                text,
                order: index,
            })),
        } as any;
    }

    if (linkPreviewEnabled) {
        createdMessage.sendLinkPreview = true;

        if (options.scheduled) {
            // A SCHEDULED message must carry the resolved preview ON the stored
            // doc so the scheduled-messages UI renders the card right away
            // (matching the product editor's "Update Link Preview"). Resolve it
            // here via WhatsApp's crawler and store it. Direct sends instead
            // leave `linkPreview` empty and let sendMessageThunkTool resolve it
            // at send time.
            const url = findFirstUrlInText(messageBody);
            if (url) {
                let fetched: EngineLinkPreviewResult | null = null;
                try {
                    const resp = await waGetLinkPreviewTool({ link: url });
                    fetched = resp.success ? (resp.data as EngineLinkPreviewResult) : null;
                } catch (error) {
                    console.warn('[DiagnosticsToolRunner] schedule link preview fetch failed:', error);
                }
                createdMessage.linkPreview = buildLinkPreview(fetched, url) as any;
                console.log('[DiagnosticsToolRunner] scheduled link preview stored', {
                    url,
                    title: (createdMessage.linkPreview as any)?.title,
                    hasThumbnail: !!(createdMessage.linkPreview as any)?.thumbnail,
                });
            } else {
                // No URL to preview — don't flag an empty card.
                createdMessage.sendLinkPreview = false;
            }
        }
    }

    return createdMessage;
};

/**
 * The raw sendMessageThunkTool result nests the same id/chatId/waMessageKey
 * at every layer: the server-mode forward wraps the engine's data under
 * `.response`, the engine wraps the raw page send-result under `.response`
 * again, and the page result repeats the keys a third time. Flatten that into
 * a single clean object — the canonical key object once, the actual
 * sent-message details once, the account info once — with no duplication.
 *
 * `waMessageKey` is the engine's key OBJECT ({fromMe, remote, id, _serialized})
 * — the same shape `waGetMessageByKeyTool` returns. The redundant flat
 * `messageId` (it only duplicated `waMessageKey._serialized`) was dropped.
 *
 * `userMessageId` is NOT a property of a sent message — it is the originating
 * Blueticks message `_id` that gets encoded as the WA `messageSecret` when no
 * explicit secret is supplied. It was dropped from the result; the `secret`
 * (caller-supplied, consumed internally as the WA messageSecret) is the only
 * correlation field surfaced here.
 */
const shapeSendMessageResponse = (
    raw: any,
    createdMessage: UserMessage,
): Record<string, any> => {
    const secret = (createdMessage as any)?.secret ?? null;

    // Error envelope ({ success:false, error }) — pass through, just surface the secret.
    if (!raw || raw.success === false) {
        return { ...(raw ?? { success: false }), secret };
    }

    const data = raw.data ?? {};

    // Descend through the re-nested wrapper layers to the leaf page-send result,
    // picking up account info (waMeInfo) from whichever layer carries it.
    let leaf: any = data;
    let waMeInfo: any;
    while (leaf && typeof leaf === 'object' && leaf.response && typeof leaf.response === 'object') {
        if (leaf.waMeInfo && !waMeInfo) waMeInfo = leaf.waMeInfo;
        leaf = leaf.response;
    }
    if (data.waMeInfo && !waMeInfo) waMeInfo = data.waMeInfo;

    // Surface the engine's key OBJECT (same shape as waGetMessageByKeyTool).
    // Prefer whichever layer carries the structured key; fall back to a
    // minimal `{ _serialized }` when only a serialized string is available.
    const serialized =
        (typeof data.waMessageKey === 'string' && data.waMessageKey) ||
        leaf?.waMessageKey?._serialized ||
        leaf?.id ||
        data.id ||
        null;
    const waMessageKey =
        (leaf?.waMessageKey && typeof leaf.waMessageKey === 'object')
            ? leaf.waMessageKey
            : (data?.waMessageKey && typeof data.waMessageKey === 'object')
                ? data.waMessageKey
                : (serialized ? { _serialized: serialized } : null);

    return {
        success: true,
        data: {
            chatId: data.chatId ?? leaf?.chatId ?? null,
            waMessageKey,
            secret,
            sentMessage: {
                body: leaf?.body ?? null,
                caption: leaf?.caption ?? null,
                type: leaf?.type ?? null,
                timestamp: leaf?.t ?? null,
                fromMe: leaf?.fromMe ?? null,
                hasMedia: leaf?.hasAttachedMedia ?? null,
                filename: leaf?.filename ?? null,
            },
            account: waMeInfo
                ? {
                    phone: waMeInfo.phone ?? null,
                    name: waMeInfo.name ?? null,
                    platform: waMeInfo.platform ?? null,
                }
                : null,
        },
    };
};

const executeSendMessageDiagnostic = async (dispatch: AppThunkDispatch, inputs: Record<string, any>) => {
    const createdMessage = await buildDiagnosticUserMessage(dispatch, inputs, { scheduled: false });
    // Typing-simulation options. `withTyping` defaults ON; `typingSeconds`
    // (number, default 0) is added on top of the built-in random jitter.
    const withTyping = inputs.withTyping === undefined ? true : toBoolean(inputs.withTyping);
    const typingSecondsRaw = Number(inputs.typingSeconds);
    const typingSeconds = Number.isFinite(typingSecondsRaw) ? typingSecondsRaw : 0;
    const raw = await sendMessageThunkTool({
        userMessage: createdMessage,
        options: { withTyping, typingSeconds },
    });
    return shapeSendMessageResponse(raw, createdMessage);
};

const executeScheduleMessageDiagnostic = async (dispatch: AppThunkDispatch, inputs: Record<string, any>) => {
    const createdMessage = await buildDiagnosticUserMessage(dispatch, inputs, { scheduled: true });
    return dispatch(scheduleMessageTool(createdMessage as Parameters<typeof scheduleMessageTool>[0])).unwrap();
};

// Tool executor map - maps test IDs to tool dispatchers
type ToolExecutor = (dispatch: AppThunkDispatch, inputs: Record<string, any>) => Promise<any>;

const toolExecutors: Record<string, ToolExecutor> = {
    // ── Validation ──────────────────────────────────────────────────────────
    'validation.validateChatId': async (dispatch, i) =>
        (await waValidatePhoneNumberOrChatIdTool({ chatId: i.chatId })),

    'validation.getLinkPreview': async (dispatch, i) =>
        (await waGetLinkPreviewTool({ link: i.content })),

    // ── Chat (Profile Pic) ──────────────────────────────────────────────────
    'chat.getProfilePicUrl': async (dispatch, i) => {
        const raw = i.chatIds ?? i.contactIds;
        const ids = Array.isArray(raw) ? raw : [raw].filter(Boolean);
        return await waGetProfilePicUrlTool({ chatIds: ids });
    },

    // ── Chat ────────────────────────────────────────────────────────────────
    'chat.getChatObjectByChatId': async (dispatch, i) =>
        (await waGetChatObjectByChatIdTool({ chatId: i.chatId })),

    'chat.getChatParticipants': async (dispatch, i) =>
        (await waGetChatParticipantsTool({ chatId: i.chatId, limit: i.limit, offset: i.offset })),

    // ── Latest Chats + Messages (composite, one round-trip) ────────────
    'chat.getLatestChatsAndMessages': async (dispatch, i) =>
        (await waGetLatestChatsAndMessagesTool({
            numberOfChats: i.numberOfChats ?? 50,
            numberOfMessages: i.numberOfMessages ?? 20,
        })),

    // ── Contact/Chat Query Tool ────────────────────────────────────────
    'chat.queryContactsAndChats': async (dispatch, i) =>
        (await waQueryContactsAndChatsTool(buildQueryContactsAndChatsParams(i))),

    // ── Newsletter (Channel) ───────────────────────────────────────────
    'newsletter.getNewsletter': async (dispatch, i) => {
        const newsletterId = String(i.newsletterId || '').trim();
        if (!newsletterId) return { error: 'newsletterId is required' } as any;
        if (isWhatsappExtensionContext()) {
            const resp = await WhatsappToolsClientActions.waGetNewsletterTool({ newsletterId });
            return unwrapEngineResult(resp);
        }
        // Server mode: backend -> MQTT -> Engine -> WhatsApp
        return await triggerRequesthWhatsappTool<{ newsletterId: string }, WaGetNewsletterResult>(
            WA_TOOL_COMMANDS.waGetNewsletterTool,
            { newsletterId },
        );
    },

    // ── Message Query Tool ────────────────────────────────────────────
    // Simplified API that hides internal implementation details
    'message.queryMessages': async (dispatch, i) => {
        // Flat contract shape (the legacy nested form is gone).
        const params: WaToolArgs<typeof WA_TOOL_COMMANDS.waQueryMessagesTool> = {
            chatId: i.chatId,
            limit: i.limit ?? 20,
            offset: i.offset ?? 0,
        };

        // Search mode — flat searchQuery; the engine resolves it against
        // the optional `chatId` when present.
        if (i.search) {
            params.searchQuery = i.search;
        }

        // Window origin: `history` pages from the oldest message, `latest`
        // (default) from the newest. Rows are always ascending.
        if (i.order === 'asc') {
            params.mode = 'history';
        }

        // Date filters
        if (i.after) params.cutoffDate = i.after;
        if (i.before) params.latestDate = i.before;

        // Load from phone if needed (when not enough messages in store)
        if (i.loadFromPhoneIfNeeded) {
            params.loadFromPhoneIfNeeded = true;
        }

        return (await waQueryMessagesTool(params));
    },

    // ── Load Messages From Phone Tool ──────────────────────────────────────────
    'message.loadMessagesFromPhone': async (dispatch, i) =>
        (await waLoadMessagesFromPhoneTool({ chatId: i.chatId })),

    'chat.sendSeen': async (dispatch, i) =>
        (await waSendSeenTool({ chatId: i.chatId })),

    'chat.clearMessages': async (dispatch, i) =>
        (await waClearMessagesTool({ chatId: i.chatId })),

    'chat.deleteChat': async (dispatch, i) =>
        (await waDeleteChatTool({ chatId: i.chatId })),

    'chat.archiveChat': async (dispatch, i) =>
        (await waArchiveChatTool({ chatId: i.chatId })),

    'chat.unarchiveChat': async (dispatch, i) =>
        (await waUnarchiveChatTool({ chatId: i.chatId })),

    'chat.pinChat': async (dispatch, i) =>
        (await waPinChatTool({ chatId: i.chatId })),

    'chat.unpinChat': async (dispatch, i) =>
        (await waUnpinChatTool({ chatId: i.chatId })),

    'chat.muteChat': async (dispatch, i) => {
        const unmuteDate = String(i.unmuteDate || '').trim();
        return await waMuteChatTool({
            chatId: i.chatId,
            unmuteDate: unmuteDate || undefined,
        });
    },

    'chat.unmuteChat': async (dispatch, i) =>
        (await waUnmuteChatTool({ chatId: i.chatId })),

    'chat.markChatUnread': async (dispatch, i) =>
        (await waMarkChatUnreadTool({ chatId: i.chatId })),

    'chat.sendStateTyping': async (dispatch, i) =>
        (await waSendStateTypingTool({ chatId: i.chatId })),

    'chat.sendStateRecording': async (dispatch, i) =>
        (await waSendStateRecordingTool({ chatId: i.chatId })),

    'chat.clearState': async (dispatch, i) =>
        (await waClearStateTool({ chatId: i.chatId })),

    'chat.getChatLabels': async (dispatch, i) =>
        (await waGetChatLabelsTool({ chatId: i.chatId })),

    'chat.changeLabels': async (dispatch, i) => {
        const labelIds = parseCsvValues(i.labelIdsCommaSeparated || i.labelIds);
        if (labelIds.length === 0) return { error: 'labelIdsCommaSeparated is required' } as any;
        return await waChangeLabelsTool({ chatId: i.chatId, labelIds });
    },

    'chat.getPinnedMessages': async (dispatch, i) =>
        (await waGetPinnedMessagesTool({ chatId: i.chatId })),

    'chat.addOrEditCustomerNote': async (dispatch, i) => {
        if (!i.note) return { error: 'note is required' } as any;
        return await waAddOrEditCustomerNoteTool({ chatId: i.chatId, note: String(i.note) });
    },

    'chat.openChat': async (dispatch, i) =>
        (await waOpenChatTool({ chatId: i.chatId })),

    // ── Group ───────────────────────────────────────────────────────────────
    'group.create': (dispatch, i) => {
        const groupName = String(i.groupName || '').trim();
        const participantChatIds = parseCsvValues(
            i.participantChatIdsCommaSeparated || i.participantChatIdsCsv || i.participantChatIds
        );
        const missing = [
            ...(!groupName ? ['groupName'] : []),
            ...(participantChatIds.length === 0 ? ['participantChatIdsCommaSeparated'] : []),
        ];
        if (missing.length) return { error: `${missing.join(' and ')} is required` } as any;
        return waCreateGroupTool({ groupName, participantChatIds });
    },

    'group.updateName': (dispatch, i) => {
        const missing = [...(!i.groupId ? ['groupId'] : []), ...(!i.groupName ? ['groupName'] : [])];
        if (missing.length) return { error: `${missing.join(' and ')} is required` } as any;
        return waUpdateGroupNameTool({ groupId: i.groupId, groupName: i.groupName });
    },

    'group.getData': (dispatch, i) => {
        if (!i.groupId) return { error: 'groupId is required' } as any;
        return waGetGroupDataTool({ groupId: i.groupId });
    },

    'group.getCommonGroups': (dispatch, i) => {
        if (!i.contactChatId) return { error: 'contactChatId is required' } as any;
        return waGetCommonGroupsTool({ contactChatId: i.contactChatId });
    },

    'group.updateSettings': (dispatch, i) => {
        const desc = i.settingsDescription ?? i.settingsJson ?? i.settings;
        const missing = [...(!i.groupId ? ['groupId'] : []), ...((desc === undefined || desc === null) ? ['settingsDescription'] : [])];
        if (missing.length) return { error: `${missing.join(' and ')} is required` } as any;
        const settingsPayload = desc === '' ? { __mockSettingsText: '' } : parseGroupSettingsInput(desc);
        return waUpdateGroupDescriptionTool({ groupId: i.groupId, settings: settingsPayload });
    },

    'group.addParticipant': (dispatch, i) => {
        const missing = [...(!i.groupId ? ['groupId'] : []), ...(!i.participantChatId ? ['participantChatId'] : [])];
        if (missing.length) return { error: `${missing.join(' and ')} is required` } as any;
        return waAddGroupParticipantTool({ groupId: i.groupId, participantChatId: i.participantChatId });
    },

    'group.removeParticipant': (dispatch, i) => {
        const missing = [...(!i.groupId ? ['groupId'] : []), ...(!i.participantChatId ? ['participantChatId'] : [])];
        if (missing.length) return { error: `${missing.join(' and ')} is required` } as any;
        return waRemoveGroupParticipantTool({ groupId: i.groupId, participantChatId: i.participantChatId });
    },

    'group.setAdminRights': (dispatch, i) => {
        const missing = [...(!i.groupId ? ['groupId'] : []), ...(!i.participantChatId ? ['participantChatId'] : [])];
        if (missing.length) return { error: `${missing.join(' and ')} is required` } as any;
        return waSetGroupAdminRightsTool({ groupId: i.groupId, participantChatId: i.participantChatId });
    },

    'group.removeAdminRights': (dispatch, i) => {
        const missing = [...(!i.groupId ? ['groupId'] : []), ...(!i.participantChatId ? ['participantChatId'] : [])];
        if (missing.length) return { error: `${missing.join(' and ')} is required` } as any;
        return waRemoveGroupAdminRightsTool({ groupId: i.groupId, participantChatId: i.participantChatId });
    },

    'group.setPicture': async (dispatch, i) => {
        const missing = [...(!i.groupId ? ['groupId'] : []), ...(!i.fileDataUrl ? ['fileDataUrl'] : [])];
        if (missing.length) return { error: `${missing.join(' and ')} is required` } as any;
        return waSetGroupPictureTool({
            groupId: i.groupId,
            fileDataUrl: await ensureGroupPictureFileDataUrl(i.fileDataUrl),
            fileName: i.fileName,
            fileMimeType: i.fileMimeType,
        });
    },

    'group.leave': (dispatch, i) => {
        if (!i.groupId) return { error: 'groupId is required' } as any;
        return waLeaveGroupTool({ groupId: i.groupId });
    },

    // ── Message Retrieval ───────────────────────────────────────────────────
    // NOTE: message.getMessages_history, message.getMessages_latest, message.searchMessages
    // have been unified into message.queryMessages (see above)

    'message.getWaMessageByWaMessageKey': async (dispatch, i) =>
        (await waGetMessageByKeyTool({
            waMessageKey: resolveMessageKeyInput(i),
        })),

    'message.messageStoreLookup': async (dispatch, i) =>
        (await waGetUnloggedWaMessageKeyIfExistInStoreTool({
            chatId: i.chatId,
            userMessageId: i.userMessageId,
            dueDate: i.dueDate ? new Date(i.dueDate) : new Date(),
            supportedUserId: i?.supportedUserId,
        })),

    'message.reactToMessage': async (dispatch, i) =>
        (await waReactToMessageThunkTool({
            waMessageKey: resolveMessageKeyInput(i),
            text: i.text,
        })),

    'message.forwardMessage': async (dispatch, i) =>
        (await waForwardMessageTool({
            waMessageKey: resolveMessageKeyInput(i),
            // Diagnostics passes destination chats as a comma-separated string.
            toChatIds: typeof i.toChatIds === 'string'
                ? i.toChatIds.split(',').map((s: string) => s.trim()).filter(Boolean)
                : (Array.isArray(i.toChatIds) ? i.toChatIds : []),
        })),

    'message.starMessage': async (dispatch, i) =>
        (await waStarMessageTool({
            waMessageKey: resolveMessageKeyInput(i),
            // Diagnostics inputs arrive stringly-typed; default to star.
            star: i.star !== false && i.star !== 'false',
        })),

    'message.deleteMessage': async (dispatch, i) =>
        (await waDeleteMessageTool({
            waMessageKey: resolveMessageKeyInput(i),
            forEveryone: i.forEveryone === true || i.forEveryone === 'true',
        })),

    'message.pinMessage': async (dispatch, i) =>
        (await waPinMessageTool({
            waMessageKey: resolveMessageKeyInput(i),
            duration: i.duration != null && i.duration !== '' ? Number(i.duration) : undefined,
        })),

    'message.unpinMessage': async (dispatch, i) =>
        (await waUnpinMessageTool({
            waMessageKey: resolveMessageKeyInput(i),
        })),

    'status.createStatus': async (dispatch, i) =>
        (await createStatusTool({
            supportedUserId: i?.supportedUserId,
            text: i.text,
            fileDataUrl: i.fileDataUrl,
            fileName: i.fileName,
            fileMimeType: i.fileMimeType,
        })),

    'message.getMessageAck': async (dispatch, i) =>
        (await waGetMessageAckTool({
            waMessageKey: resolveMessageKeyInput(i),
        })),

    // ── Media ───────────────────────────────────────────────────────────────
    'media.getMessageMedia': async (dispatch, i) =>
        (await waGetMessageMediaTool({ waMessageKey: i.msgId, mode: 'preview' })),

    // ── Session ─────────────────────────────────────────────────────────────
    'session.getWaMeInfo': async (dispatch, i) =>
        (await getWaMeInfoTool({ supportedUserId: i?.supportedUserId })),

    'session.isWhatsAppConnected': async (dispatch, i) =>
        (await isWhatsAppConnectedThunkTool({ supportedUserId: i?.supportedUserId })),

    'session.validateStores': async (dispatch, _i) =>
        (await waValidateStoresTool()),

    'session.reloadPage': (dispatch, i) =>
        waReloadPageTool({
            reason: i.reason || 'diagnostics',
        }),

    'session.triggerQRTimeout': async (dispatch, _i) =>
        (await waTriggerQRTimeoutTool()),

    'session.triggerWaLogout': async (dispatch, i) =>
        (await waTriggerLogoutTool({ supportedUserId: i?.supportedUserId })),

    // ── Message Sending (write) ─────────────────────────────────────────────
    'message.sendMessage': (dispatch, i) =>
        executeSendMessageDiagnostic(dispatch, i),
    // Legacy aliases keep backward compatibility for callers using older IDs.
    'message.sendTextMessage': (dispatch, i) =>
        executeSendMessageDiagnostic(dispatch, i),
    'message.sendImageMessage': (dispatch, i) =>
        executeSendMessageDiagnostic(dispatch, {
            ...i,
            fileDataUrl: i.fileDataUrl || DIAGNOSTIC_TEST_IMAGE_DATA_URL,
        }),
    'message.sendVideoMessage': (dispatch, i) =>
        executeSendMessageDiagnostic(dispatch, i),
    'message.sendPollMessage': (dispatch, i) =>
        executeSendMessageDiagnostic(dispatch, i),
    'message.sendLinkPreviewMessage': (dispatch, i) =>
        executeSendMessageDiagnostic(dispatch, i),

    // ── Message Scheduling (write - stores in DB for later delivery) ─────
    'schedule.scheduleMessage': (dispatch, i) =>
        executeScheduleMessageDiagnostic(dispatch, i),
    // Legacy aliases keep backward compatibility for callers using older IDs.
    'schedule.scheduleTextMessage': (dispatch, i) =>
        executeScheduleMessageDiagnostic(dispatch, i),
    'schedule.scheduleImageMessage': (dispatch, i) =>
        executeScheduleMessageDiagnostic(dispatch, {
            ...i,
            fileDataUrl: i.fileDataUrl || DIAGNOSTIC_TEST_IMAGE_DATA_URL,
        }),
    'schedule.schedulePollMessage': (dispatch, i) =>
        executeScheduleMessageDiagnostic(dispatch, i),
    'schedule.scheduleLinkPreviewMessage': (dispatch, i) =>
        executeScheduleMessageDiagnostic(dispatch, i),
    // ── Label ───────────────────────────────────────────────────────────────
    'label.updateShouldAppearChatsByList': async (dispatch, i) =>
        (await waUpdateShouldAppearChatsByListTool(i.chatsList)),
};

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error(`Test timed out after ${ms}ms`)), ms);
        promise.then(
            (val) => { clearTimeout(timer); resolve(val); },
            (err) => { clearTimeout(timer); reject(err); },
        );
    });
}

/**
 * Topologically sort tests by their dependencies.
 */
function topologicalSort(tests: DiagnosticTestDefinition[]): DiagnosticTestDefinition[] {
    const idSet = new Set(tests.map(t => t.id));
    const sorted: DiagnosticTestDefinition[] = [];
    const visited = new Set<string>();
    const visiting = new Set<string>();
    const testMap = new Map(tests.map(t => [t.id, t]));

    function visit(test: DiagnosticTestDefinition) {
        if (visited.has(test.id)) return;
        if (visiting.has(test.id)) return;
        visiting.add(test.id);

        for (const depId of test.dependencies ?? []) {
            const dep = testMap.get(depId);
            if (dep && idSet.has(depId)) {
                visit(dep);
            }
        }

        visiting.delete(test.id);
        visited.add(test.id);
        sorted.push(test);
    }

    for (const test of tests) {
        visit(test);
    }

    return sorted;
}

/**
 * Sanitize response data to avoid massive payloads.
 */
function sanitizeResponse(response: any): any {
    if (response === null || response === undefined) return response;

    try {
        const trimmed = trimArrays(response);
        const json = JSON.stringify(trimmed);
        if (json.length > 2_000) {
            return { _truncated: true, _originalSize: json.length, preview: json.slice(0, 2_000) };
        }
        return JSON.parse(json);
    } catch {
        return { _type: typeof response, _toString: String(response).slice(0, 300) };
    }
}

function trimArrays(obj: any, maxItems = 3): any {
    if (obj === null || obj === undefined) return obj;
    if (Array.isArray(obj)) {
        const sliced = obj.slice(0, maxItems);
        const result = sliced.map(item => trimArrays(item, maxItems));
        if (obj.length > maxItems) {
            result.push({ _more: obj.length - maxItems });
        }
        return result;
    }
    if (typeof obj === 'object') {
        const out: any = {};
        for (const key of Object.keys(obj)) {
            if (key.toLowerCase() === 'filedataurl') {
                // Never show fileDataUrl in diagnostics payload previews.
                continue;
            }
            out[key] = trimArrays(obj[key], maxItems);
        }
        return out;
    }
    return obj;
}

function normalizeDiagnosticError(err: any): { message: string; stack?: string } {
    if (!err) {
        return { message: 'Unknown error' };
    }

    if (typeof err === 'string') {
        return { message: err };
    }

    if (err instanceof Error) {
        return { message: err.message || 'Unknown error', stack: err.stack };
    }

    const candidates = [
        err?.message,
        err?.errorMessage,
        typeof err?.error === 'string' ? err.error : err?.error?.message,
        err?.data?.message,
        typeof err?.data?.error === 'string' ? err.data.error : err?.data?.error?.message,
    ].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

    if (candidates.length > 0) {
        return { message: candidates[0], stack: err?.stack || err?.data?.stack };
    }

    try {
        return { message: JSON.stringify(err), stack: err?.stack || err?.data?.stack };
    } catch {
        return { message: String(err), stack: err?.stack || err?.data?.stack };
    }
}

/**
 * Run diagnostics by dispatching tools.
 * This is the content-script-compatible runner that uses tools as the single authority.
 */
export async function runDiagnosticsWithTools(
    dispatch: AppThunkDispatch,
    options: DiagnosticRunOptions = {}
): Promise<DiagnosticRunResult> {
    const startTime = Date.now();
    const safetyLevels: TestSafety[] = options.safetyLevels ?? ['safe'];
    const trimResponse = options.trimResponse !== false;

    console.log('[DiagnosticsToolRunner] Starting diagnostic run with full options:', JSON.stringify({
        safetyLevels,
        domains: options.domains,
        testIds: options.testIds,
        inputOverrides: options.inputOverrides ? Object.keys(options.inputOverrides) : undefined,
        trimResponse: options.trimResponse,
    }));

    // ── Step 1: Filter tests ─────────────────────────────────────────────
    let tests = diagnosticTests.filter(t => t.enabled);
    console.log(`[DiagnosticsToolRunner] After enabled filter: ${tests.length} tests`);

    tests = tests.filter(t => safetyLevels.includes(t.safety));
    console.log(`[DiagnosticsToolRunner] After safety filter (${safetyLevels.join(',')}): ${tests.length} tests`);

    if (options.domains?.length) {
        tests = tests.filter(t => options.domains!.includes(t.domain));
        console.log(`[DiagnosticsToolRunner] After domains filter: ${tests.length} tests`);
    }

    if (options.testIds?.length) {
        console.log(`[DiagnosticsToolRunner] Filtering by testIds: ${options.testIds.join(', ')}`);
        tests = tests.filter(t => options.testIds!.includes(t.id));
        console.log(`[DiagnosticsToolRunner] After testIds filter: ${tests.length} tests`);
    }

    console.log(`[DiagnosticsToolRunner] ${tests.length} tests selected after filtering, IDs: ${tests.map(t => t.id).join(', ')}`);

    // ── Step 2: Topological sort ──────────────────────────────────────────
    const sorted = topologicalSort(tests);

    // ── Step 2.5: Resolve smart defaults (ownPhone, ownChatId, etc.) ─────
    let defaults: ResolvedDefaults = {
        ownPhone: undefined,
        ownChatId: undefined,
        ownChatIdArray: undefined,
        firstChatId: undefined,
        firstGroupChatId: undefined,
        firstNewsletterId: undefined,
        latestMessageKey: undefined,
        latestOwnMessage: undefined,
        testImageDataUrl: DIAGNOSTIC_TEST_IMAGE_DATA_URL,
        futureDate: new Date(Date.now() + 60 * 60 * 1000),
    };
    try {
        defaults = await resolveAllDefaults();
        console.log('[DiagnosticsToolRunner] Resolved defaults:', Object.keys(defaults));
    } catch (err) {
        console.warn('[DiagnosticsToolRunner] Failed to resolve defaults:', err);
    }

    // ── Step 3: Execute ───────────────────────────────────────────────────
    const results: DiagnosticTestResult[] = [];
    const passedIds = new Set<string>();
    const failedIds = new Set<string>();

    for (const test of sorted) {
        // Check dependencies
        const depsFailed = (test.dependencies ?? []).some(depId => failedIds.has(depId));

        if (depsFailed) {
            results.push({
                id: test.id,
                name: test.name,
                domain: test.domain,
                status: 'skipped',
                duration: 0,
                error: { message: 'Skipped: dependency failed' },
                safety: test.safety,
            });
            failedIds.add(test.id);
            continue;
        }

        // Resolve inputs from overrides, defaultResolver, or defaultValue
        const inputs: Record<string, any> = {};
        for (const input of test.inputs) {
            if (options.inputOverrides?.[test.id]?.[input.name] !== undefined) {
                inputs[input.name] = options.inputOverrides[test.id][input.name];
            } else if (input.defaultResolver) {
                // Use smart defaults (e.g., ownPhone, ownChatId, firstChatId)
                const val = resolveDefault(input.defaultResolver, defaults);
                if (val !== undefined) {
                    inputs[input.name] = val;
                }
            } else if (input.defaultValue !== undefined) {
                inputs[input.name] = input.defaultValue;
            }
        }

        // Sanitize inputs
        const safeInputs = sanitizeResponse(inputs);

        // Get tool executor
        const executor = toolExecutors[test.id];
        if (!executor) {
            results.push({
                id: test.id,
                name: test.name,
                domain: test.domain,
                status: 'error',
                duration: 0,
                error: { message: `No tool executor found for test: ${test.id}` },
                safety: test.safety,
                inputs: safeInputs,
            });
            failedIds.add(test.id);
            continue;
        }

        // Execute
        const testStart = Date.now();
        const timeout = test.timeoutMs ?? options.timeoutMs ?? DEFAULT_TEST_TIMEOUT_MS;

        try {
            const response = await withTimeout(executor(dispatch, inputs), timeout);
            const duration = Date.now() - testStart;
            const hasErrorInResponse = response && typeof response === 'object' && ('error' in response || ('isValid' in response && !response.isValid));
            const status = hasErrorInResponse ? 'error' : 'pass';

            // Extract error details from the response when the tool returned an error-shaped object
            let errorFromResponse: { message: string; stack?: string } | undefined;
            if (hasErrorInResponse && response) {
                const errVal = (response as any).error;
                if (typeof errVal === 'string' && errVal.trim().length > 0) {
                    errorFromResponse = { message: errVal };
                } else if (errVal && typeof errVal === 'object') {
                    errorFromResponse = normalizeDiagnosticError(errVal);
                } else if ('isValid' in (response as any) && !(response as any).isValid) {
                    const msg = (response as any).validationError || (response as any).message || 'Validation failed (isValid: false)';
                    errorFromResponse = { message: msg };
                }
            }

            results.push({
                id: test.id,
                name: test.name,
                domain: test.domain,
                status,
                duration,
                response: trimResponse ? sanitizeResponse(response) : response,
                ...(errorFromResponse ? { error: errorFromResponse } : {}),
                safety: test.safety,
                inputs: safeInputs,
            });
            if (status === 'pass') passedIds.add(test.id); else failedIds.add(test.id);
            console.log(`[DiagnosticsToolRunner] ${status.toUpperCase()}: ${test.id} (${duration}ms)`);
        } catch (err: any) {
            const duration = Date.now() - testStart;
            const normalizedError = normalizeDiagnosticError(err);

            results.push({
                id: test.id,
                name: test.name,
                domain: test.domain,
                status: 'error',
                duration,
                error: {
                    message: normalizedError.message,
                    stack: normalizedError.stack,
                },
                safety: test.safety,
                inputs: safeInputs,
            });
            failedIds.add(test.id);
            console.error(`[DiagnosticsToolRunner] ERROR: ${test.id} (${duration}ms):`, normalizedError.message, err);
        }
    }

    // ── Step 4: Build summary ─────────────────────────────────────────────
    const summary = {
        total: results.length,
        passed: results.filter(r => r.status === 'pass').length,
        failed: results.filter(r => r.status === 'fail').length,
        errored: results.filter(r => r.status === 'error').length,
        skipped: results.filter(r => r.status === 'skipped').length,
    };

    const runResult: DiagnosticRunResult = {
        results,
        summary,
        durationMs: Date.now() - startTime,
    };

    console.log('[DiagnosticsToolRunner] Run complete:', summary);
    return runResult;
}

export function getToolExecutor(testId: string): ToolExecutor | undefined {
    return toolExecutors[testId];
}
